import { useState } from 'react';
import { FiCopy, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';

const MODALITIES = ['CR', 'CT', 'MR', 'US', 'DX', 'OT'];
const VIETNAMESE_NAMES = ['Nguyễn Văn An', 'Trần Thị Bình', 'Lê Minh Khôi', 'Phạm Thu Hà', 'Đỗ Hải Nam'];
const VIETNAMESE_REASONS = ['Đau ngực kéo dài', 'Đau đầu dữ dội', 'Khó thở khi gắng sức', 'Đau bụng cấp', 'Chấn thương vùng cổ'];
const VIETNAMESE_ADDRESSES = [
  '123 Đường Lê Lợi, Quận 1, TP. Hồ Chí Minh',
  '456 Phố Huế, Quận Hai Bà Trưng, Hà Nội',
  '789 Đường Nguyễn Văn Linh, Quận Thanh Khê, Đà Nẵng',
  '101 Đường Trần Hưng Đạo, TP. Huế',
  '202 Đường 30/4, Quận Ninh Kiều, Cần Thơ'
];

const randomPick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const nowIsoLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 19);
};

const buildRandomHisPayload = () => {
  const modality = randomPick(MODALITIES);
  const patientId = `BN${randomNumber(100000, 999999)}`;
  const now = new Date();
  const birthYear = now.getFullYear() - randomNumber(18, 80);
  
  return {
    external_id: `HIS-ORD-${randomNumber(100000, 999999)}`,
    patient_id: patientId,
    patient_name: randomPick(VIETNAMESE_NAMES),
    patient_birth_date: `${birthYear}-${String(randomNumber(1, 12)).padStart(2, '0')}-${String(randomNumber(1, 28)).padStart(2, '0')}`,
    patient_sex: randomPick(['M', 'F']),
    cccd: `0${randomNumber(10000000000, 99999999999)}`,
    patient_address: randomPick(VIETNAMESE_ADDRESSES),
    patient_phone: `09${randomNumber(10000000, 99999999)}`,
    site_id: 'SITE01',
    modality,
    scheduled_time: nowIsoLocal(),
    description: `Chỉ định ${modality} - kiểm tra tổng quát`,
    accession_number: `ACC${now.getFullYear()}${randomNumber(10000, 99999)}`,
    study_instance_uid: `1.3.6.1.4.1.34714.${randomNumber(100000, 999999)}.${randomNumber(100000, 999999)}`,
    referring_physician_name: 'BS. Nguyễn Văn Minh',
    reason_for_study: randomPick(VIETNAMESE_REASONS),
    clinical_info: 'Bệnh nhân có triệu chứng tăng dần trong 3 ngày gần đây. Đề nghị chụp kiểm tra.',
  };
};

const buildRandomPacsPayload = () => {
  const modality = randomPick(MODALITIES);
  const patientId = `BN${randomNumber(100000, 999999)}`;
  const now = new Date();
  const birthYear = now.getFullYear() - randomNumber(18, 80);

  return {
    external_id: `PACS-${randomNumber(10000, 99999)}`,
    patient_id: patientId,
    patient_name: randomPick(VIETNAMESE_NAMES),
    patient_birth_date: `${birthYear}-${String(randomNumber(1, 12)).padStart(2, '0')}-${String(randomNumber(1, 28)).padStart(2, '0')}`,
    patient_sex: randomPick(['M', 'F']),
    cccd: `0${randomNumber(10000000000, 99999999999)}`,
    patient_address: randomPick(VIETNAMESE_ADDRESSES),
    patient_phone: `09${randomNumber(10000000, 99999999)}`,
    site_id: 'SITE01',
    modality,
    accession_number: `PACS${now.getFullYear()}${randomNumber(1000, 9999)}`,
    study_instance_uid: `1.3.6.1.4.1.34714.${randomNumber(100000, 999999)}.${randomNumber(100000, 999999)}`,
    description: `Nhận ca chụp ${modality} từ PACS khác`,
    reason_for_study: randomPick(VIETNAMESE_REASONS),
    clinical_info: 'Thông tin lâm sàng gửi kèm từ hệ thống PACS nguồn.',
    dicom_files: [
      {
        file_name: `instance-${randomNumber(1, 9)}.dcm`,
        dicom_file_base64: 'R0lGODlhAQABAAAAACw=',
      },
      {
        file_name: `instance-${randomNumber(10, 99)}.dcm`,
        dicom_file_base64: 'R0lGODlhAQABAAAAACw=',
      },
    ],
  };
};

const PayloadGenerator = () => {
  const [hisPayload, setHisPayload] = useState(JSON.stringify(buildRandomHisPayload(), null, 2));
  const [pacsPayload, setPacsPayload] = useState(JSON.stringify(buildRandomPacsPayload(), null, 2));

  const copyPayload = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Đã copy ${label}`);
    } catch {
      toast.error(`Không thể copy ${label}`);
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        <div className="w-full space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700">
            Trang sinh payload tự động với dữ liệu tiếng Việt có dấu, phục vụ tích hợp API.
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">PAYLOAD tạo chỉ định HIS</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setHisPayload(JSON.stringify(buildRandomHisPayload(), null, 2))}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-700 hover:bg-slate-50"
                >
                  <FiRefreshCw /> Random payload
                </button>
                <button
                  onClick={() => copyPayload(hisPayload, 'payload HIS')}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md text-sm font-semibold hover:bg-primary-dark"
                >
                  <FiCopy /> Copy
                </button>
              </div>
            </div>
            <textarea
              value={hisPayload}
              onChange={(e) => setHisPayload(e.target.value)}
              rows={14}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-xs font-mono focus:outline-none focus:border-primary"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">PAYLOAD upload ca chụp từ PACS khác</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPacsPayload(JSON.stringify(buildRandomPacsPayload(), null, 2))}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-700 hover:bg-slate-50"
                >
                  <FiRefreshCw /> Random payload
                </button>
                <button
                  onClick={() => copyPayload(pacsPayload, 'payload PACS')}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md text-sm font-semibold hover:bg-primary-dark"
                >
                  <FiCopy /> Copy
                </button>
              </div>
            </div>
            <textarea
              value={pacsPayload}
              onChange={(e) => setPacsPayload(e.target.value)}
              rows={14}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-xs font-mono focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayloadGenerator;
